import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Save, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const TradeIdeasAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const logoSrc = '/logo.png';

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0], // Today's date
    idea_number: 1,
    title: '',
    content: '',
    category: 'Equities', // Default category
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Admin email - only this user can create trade ideas
  const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'your-email@example.com';

  // Fetch next unique idea number on mount
  useEffect(() => {
    const fetchNextIdeaNumber = async () => {
      const { data, error } = await supabase
        .from('trade_ideas')
        .select('idea_number')
        .order('idea_number', { ascending: false })
        .limit(1);

      if (!error && data && data.length > 0) {
        const nextNumber = data[0].idea_number + 1;
        setFormData(prev => ({
          ...prev,
          idea_number: nextNumber
        }));
      }
    };

    if (user && user.email === ADMIN_EMAIL) {
      fetchNextIdeaNumber();
    }
  }, [user, ADMIN_EMAIL]);

  // Redirect if not logged in or not admin
  if (!user || user.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen bg-[#111315] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-neutral-400 mb-6">
            {!user ? 'You must be logged in to access this page.' : 'Only administrators can create trade ideas.'}
          </p>
          <Link
            to={!user ? "/login" : "/trade-ideas"}
            className="px-6 py-3 bg-[#CBB26A] text-black font-bold rounded-lg hover:bg-[#A8914D] transition"
          >
            {!user ? 'Go to Login' : 'Back to Trade Ideas'}
          </Link>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'idea_number' ? parseInt(value) : value
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Add new files to existing ones
      setImageFiles(prev => [...prev, ...files]);

      // Create previews for all new files
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, { file: file.name, preview: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    if (imageFiles.length === 0) return [];

    try {
      const uploadPromises = imageFiles.map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = fileName;

        const { error } = await supabase.storage
          .from('blog-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          console.error('Upload error:', error);
          throw new Error(`Failed to upload ${file.name}: ${error.message}`);
        }

        const { data: urlData } = supabase.storage
          .from('blog-images')
          .getPublicUrl(filePath);

        return urlData.publicUrl;
      });

      return await Promise.all(uploadPromises);
    } catch (err) {
      console.error('uploadImages error:', err);
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Upload images if present
      const image_urls = await uploadImages();

      // Insert blog entry
      const { error } = await supabase
        .from('trade_ideas')
        .insert([
          {
            date: formData.date,
            idea_number: formData.idea_number,
            title: formData.title,
            content: formData.content,
            category: formData.category,
            image_urls: image_urls,
            created_by: user.id
          }
        ]);

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('An example with this number already exists for this date. Please use a different example number.');
        }
        throw error;
      }

      setSuccess('Trade idea created successfully!');

      // Fetch the new next example number
      const { data: nextData } = await supabase
        .from('trade_ideas')
        .select('idea_number')
        .order('idea_number', { ascending: false })
        .limit(1);

      const nextExampleNumber = nextData && nextData.length > 0 ? nextData[0].idea_number + 1 : 1;

      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        idea_number: nextExampleNumber,
        title: '',
        content: '',
        category: 'Equities',
      });
      setImageFiles([]);
      setImagePreviews([]);

      // Redirect to blog page after 2 seconds
      setTimeout(() => {
        navigate('/trade-ideas');
      }, 2000);

    } catch (err) {
      setError(err.message || 'Failed to create trade idea');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#111315]/95 backdrop-blur-sm border-b border-neutral-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={logoSrc} alt="HDAlgos Logo" className="h-10 w-10" />
              <span className="text-2xl font-black tracking-tight">
                <span className="text-white">HD</span>
                <span className="text-[#CBB26A]">Algos</span>
              </span>
            </Link>

            <Link
              to="/trade-ideas"
              className="flex items-center gap-2 text-neutral-400 hover:text-white transition"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Trade Ideas</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-20 px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl font-black">
              <span className="text-[#CBB26A]">Create New</span>
              <span className="text-white"> Trade Idea</span>
            </h1>
            <Link
              to="/storage-test"
              className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition text-sm"
            >
              Test Storage
            </Link>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-950 border border-red-900 rounded-lg">
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-950 border border-green-900 rounded-lg">
              <p className="text-green-300">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date, Example Number, and Category */}
            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-bold text-[#CBB26A] mb-2">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-800 rounded-lg text-white focus:border-[#CBB26A] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-[#CBB26A] mb-2">
                  Idea Number
                </label>
                <input
                  type="number"
                  name="idea_number"
                  value={formData.idea_number}
                  onChange={handleInputChange}
                  min="1"
                  required
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-800 rounded-lg text-white focus:border-[#CBB26A] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-[#CBB26A] mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-800 rounded-lg text-white focus:border-[#CBB26A] focus:outline-none"
                >
                  <option value="Equities">Equities</option>
                  <option value="FX">FX</option>
                  <option value="Rates">Rates</option>
                  <option value="Commodities">Commodities</option>
                </select>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-bold text-[#CBB26A] mb-2">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="e.g., Bullish Breakout Following Paint Bar"
                className="w-full px-4 py-3 bg-neutral-900 border border-neutral-800 rounded-lg text-white focus:border-[#CBB26A] focus:outline-none"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-bold text-[#CBB26A] mb-2">
                Images (Optional)
              </label>

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {imagePreviews.map((img, index) => (
                    <div key={index} className="relative">
                      <img
                        src={img.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg border border-neutral-700"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 rounded-lg transition"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
                        Image {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <label className="block w-full border-2 border-dashed border-neutral-700 rounded-lg p-8 text-center cursor-pointer hover:border-[#CBB26A] transition">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <Upload className="w-10 h-10 mx-auto mb-3 text-neutral-500" />
                <p className="text-neutral-400">
                  {imagePreviews.length > 0 ? 'Click to add more images' : 'Click to upload images'}
                </p>
                <p className="text-sm text-neutral-600 mt-2">
                  PNG, JPG, GIF up to 10MB each • Multiple images supported
                </p>
              </label>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-bold text-[#CBB26A] mb-2">
                Content
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                required
                rows="12"
                placeholder="Describe the trading example in detail..."
                className="w-full px-4 py-3 bg-neutral-900 border border-neutral-800 rounded-lg text-white focus:border-[#CBB26A] focus:outline-none resize-vertical"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Link
                to="/trade-ideas"
                className="px-6 py-3 bg-neutral-800 text-white font-bold rounded-lg hover:bg-neutral-700 transition"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-[#CBB26A] text-black font-bold rounded-lg hover:bg-[#A8914D] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {loading ? 'Saving...' : 'Create Entry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TradeIdeasAdmin;
